/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { queryOptions } from '@tanstack/react-query';
import type { Credentials } from '@opengeoweb/authentication';
import type { AviationProduct } from '@opengeoweb/sigmet-airmet';
import {
  getSigmetTAC,
  getAirmetTAC,
  getSigmetCancelTAC,
  getAirmetCancelTAC,
} from './api';

const LIB_KEY = 'warnings';
const API_KEY = 'aviation';

const generateAviationQueryKeys = (queryKeys: string[]): string[] =>
  [LIB_KEY, API_KEY].concat(queryKeys);

// 2) Unique keys per query
export const aviationQueryOptions = {
  getSigmetTAC: (product: AviationProduct, credentials?: Credentials) =>
    queryOptions({
      queryKey: generateAviationQueryKeys([
        'sigmet',
        'tac',
        product.uuid ?? product.firName,
      ]),
      queryFn: async (): Promise<{ data: string }> =>
        getSigmetTAC(product, credentials),
    }),
  getAirmetTAC: (product: AviationProduct, credentials?: Credentials) =>
    queryOptions({
      queryKey: generateAviationQueryKeys([
        'airmet',
        'tac',
        product.uuid ?? product.firName,
      ]),
      queryFn: async (): Promise<{ data: string }> =>
        getAirmetTAC(product, credentials),
    }),
  getSigmetCancelTAC: (product: AviationProduct, credentials?: Credentials) =>
    queryOptions({
      queryKey: generateAviationQueryKeys([
        'sigmet',
        'cancel',
        product.uuid ?? product.firName,
      ]),
      queryFn: async (): Promise<{ data: string }> =>
        getSigmetCancelTAC(product, credentials),
    }),
  getAirmetCancelTAC: (product: AviationProduct, credentials?: Credentials) =>
    queryOptions({
      queryKey: generateAviationQueryKeys([
        'airmet',
        'cancel',
        product.uuid ?? product.firName,
      ]),
      queryFn: async (): Promise<{ data: string }> =>
        getAirmetCancelTAC(product, credentials),
    }),
} as const;
